import {ComponentType, RefObject, useCallback, FocusEvent} from 'react';
import Flatpickr from 'react-flatpickr';
import {useId} from '../hooks';
import {TextFieldProps} from '../TextField';
import type {BaseDatePickerProps} from './BaseDatePicker';
import {useDateInput} from './use-date-input';
import {useFlatpickr} from './use-flatpickr';

export type DateRangeInputsProps<T = TextFieldProps> = {
    InputComponent: ComponentType<T>;
    StartInputProps?: T;
    EndInputProps?: T;
    size?: BaseDatePickerProps['size'];
    id?: string;
    renderRef: (node: HTMLInputElement | null) => void;
    startValue?: string | number | Date;
    endValue?: string | number | Date;
    startDatePlaceholder?: string;
    endDatePlaceholder?: string;
    fpRef: RefObject<Flatpickr['flatpickr']>;
    dateFormat: string;
};

export function DateRangeInputs(props: DateRangeInputsProps): React.ReactElement {
    const {
        InputComponent,
        renderRef,
        size,
        StartInputProps,
        EndInputProps,
        id,
        startValue,
        endValue,
        startDatePlaceholder,
        endDatePlaceholder,
        fpRef,
        dateFormat
    } = props;
    const generatedIdStart = useId(id, 'dateRangePickerStart');
    const generatedIdEnd = useId(id, 'dateRangePickerEnd');
    const {
        parseDate,
        formatDate,
        setDate,
        isCalendarElement
    } = useFlatpickr({
        fpRef,
        dateFormat
    });

    const {
        inputValue: startInputValue,
        handleBlur: handleStartBlur,
        handleChange: handleStartChange
    } = useDateInput({
        value: startValue,
        formatDate,
        parseDate
    });
    const {
        inputValue: endInputValue,
        handleBlur: handleEndBlur,
        handleChange: handleEndChange
    } = useDateInput({
        value: endValue,
        formatDate,
        parseDate
    });
    const setDateIfValid = useCallback((e: FocusEvent<HTMLInputElement>): void => {
        if (!isCalendarElement(e.relatedTarget)) {
            setDate([startInputValue, endInputValue], true);
        }
    }, [setDate, startInputValue, endInputValue, isCalendarElement]);

    return (
        (
            <div
                className='rangePicker'
                ref={renderRef}
            >
                <InputComponent
                    placeholder={startDatePlaceholder || 'Start Date'}
                    size={size}
                    {...StartInputProps}
                    autoComplete='off'
                    data-testid={'date-range-picker-start'}
                    id={generatedIdStart}
                    onBlur={(e: FocusEvent<HTMLInputElement>): void => {
                        handleStartBlur(e);
                        setDateIfValid(e);
                    }}
                    onChange={handleStartChange}
                    value={startInputValue}
                />
                <InputComponent
                    placeholder={endDatePlaceholder || 'End Date'}
                    size={size}
                    {...EndInputProps}
                    autoComplete='off'
                    data-testid={'date-range-picker-end'}
                    id={generatedIdEnd}
                    onBlur={(e: FocusEvent<HTMLInputElement>): void => {
                        handleEndBlur(e);
                        setDateIfValid(e);
                    }}
                    onChange={handleEndChange}
                    value={endInputValue}
                />
            </div>)
    );
}
